import { supabase } from './supabase/client';
import { toast } from 'sonner';
import { devLog, devError } from './logger';
import { isSwUpdateGuardActive, hasSwUpdatedFlag } from './swUpdateState';

let refreshSessionInFlight: Promise<{ session: any; error: any }> | null = null;
let lastApiErrorToast: { message: string; at: number } | null = null;

function showDedupedApiErrorToast(message: string): void {
  const now = Date.now();
  const DEDUPE_WINDOW_MS = 1800;
  if (
    lastApiErrorToast &&
    lastApiErrorToast.message === message &&
    now - lastApiErrorToast.at < DEDUPE_WINDOW_MS
  ) {
    devLog('ℹ️ Suppressing duplicate API error toast:', message);
    return;
  }
  lastApiErrorToast = { message, at: now };
  toast.error(message);
}

function isAbortLikeError(error: any): boolean {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    error?.name === 'AbortError' ||
    message.includes('abort') ||
    message.includes('aborted')
  );
}

function isTransientRefreshError(error: any): boolean {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    isAbortLikeError(error) ||
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('network error') ||
    message.includes('timeout') ||
    message.includes('load failed')
  );
}

function isDefinitelyInvalidRefreshToken(error: any): boolean {
  const message = (error?.message || '').toString().toLowerCase();
  return (
    error?.status === 400 ||
    error?.status === 401 ||
    message.includes('invalid refresh token') ||
    message.includes('refresh token not found') ||
    message.includes('token is expired') ||
    message.includes('jwt expired') ||
    message.includes('invalid token')
  );
}

function isTransientRequestMessage(message: string | undefined): boolean {
  const normalized = (message || '').toLowerCase();
  return (
    normalized.includes('abort') ||
    normalized.includes('aborted') ||
    normalized.includes('failed to fetch') ||
    normalized.includes('network') ||
    normalized.includes('timeout') ||
    normalized.includes('load failed') ||
    normalized.includes('temporary failure') ||
    normalized.includes('session refresh aborted during update')
  );
}

function shouldSuppressApiToastDuringSwUpdate(status: number, message?: string): boolean {
  const swGuardActive = isSwUpdateGuardActive() || hasSwUpdatedFlag();
  if (!swGuardActive) return false;

  if (status === 401 || status === 403 || status === 429 || status >= 500) {
    return true;
  }

  return isTransientRequestMessage(message);
}

async function refreshSessionDeduped() {
  if (refreshSessionInFlight) return refreshSessionInFlight;

  refreshSessionInFlight = (async () => {
    const { data, error } = await supabase.auth.refreshSession();
    return { session: data?.session ?? null, error };
  })();

  try {
    return await refreshSessionInFlight;
  } finally {
    refreshSessionInFlight = null;
  }
}

/**
 * Fetches data with automatic token refresh on 401 errors and error handling.
 * This utility ensures we always use a fresh access token and provides
 * consistent error handling across the app (rate limiting, validation, etc).
 * 
 * @param url - The URL to fetch
 * @param options - Fetch options (will be merged with auth header)
 * @returns Fetch response
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // Get fresh session from Supabase (uses autoRefreshToken internally)
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    const transientStartupState = isSwUpdateGuardActive() || hasSwUpdatedFlag();
    if (transientStartupState) {
      devLog('ℹ️ No valid session available during transient SW/auth state');
    } else {
      devError('❌ No valid session available:', sessionError);
    }
    throw new Error('Unauthorized - no valid session');
  }

  // First attempt with current token
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${session.access_token}`,
    },
  });

  // If we get 401, try to refresh and retry once
  if (response.status === 401) {
    devLog('🔄 Token expired, attempting to refresh session...');

    // Another request may already have refreshed the token.
    // Retry once with the latest token before forcing a refresh flow.
    const {
      data: { session: latestSession },
    } = await supabase.auth.getSession();
    if (latestSession?.access_token && latestSession.access_token !== session.access_token) {
      const retryWithLatestToken = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${latestSession.access_token}`,
        },
      });
      if (retryWithLatestToken.status !== 401) {
        return retryWithLatestToken;
      }
    }
    
    // Check if this is right after an update - might need a small delay for session to be available
    const justUpdated = hasSwUpdatedFlag();
    const updateInProgress = isSwUpdateGuardActive();
    
    if (justUpdated || updateInProgress) {
      devLog('⏳ Just updated or update in progress - waiting briefly for session to be available...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Increased wait after update
    }
    
    let refreshedSession: any = null;
    try {
      const refreshResult = await refreshSessionDeduped();
      refreshedSession = refreshResult.session;
      const error = refreshResult.error;
      
      if (error || !refreshedSession) {
        // ✅ CRITICAL: Check if this is an AbortError (happens during reload/update)
        const isAbortError = isAbortLikeError(error);
        
        if (isAbortError && (justUpdated || updateInProgress)) {
          devLog('⚠️ AbortError during refresh after update - this is normal, session should be restored after reload');
          // Don't throw error - let the app continue and session will be restored on next load
          throw new Error('Session refresh aborted during update - will retry after reload');
        }
        
        // Check if this is a "session missing" error after update - might be timing issue
        const isSessionMissing = error?.message?.toLowerCase().includes('session missing') || 
                                 error?.message?.toLowerCase().includes('auth session missing');
        
        if (isSessionMissing && (justUpdated || updateInProgress)) {
          devLog('⚠️ Session missing after update - retrying once more...');
          // Wait a bit longer and try one more time
          await new Promise(resolve => setTimeout(resolve, 800)); // Increased wait time
          const retryData = await refreshSessionDeduped();
          
          if (retryData.error || !retryData.session) {
            // Check if retry also failed with AbortError
            const isRetryAbortError = retryData.error?.name === 'AbortError' || 
                                      (retryData.error instanceof Error && retryData.error.name === 'AbortError');
            
            if (isRetryAbortError && (justUpdated || updateInProgress)) {
              devLog('⚠️ AbortError on retry after update - this is normal');
              throw new Error('Session refresh aborted during update - will retry after reload');
            }
            
            if (isTransientRefreshError(retryData.error)) {
              devLog('⚠️ Failed to refresh session after retry due to transient error:', retryData.error);
            } else {
              devError('❌ Failed to refresh session after retry:', retryData.error);
            }
            // Fall through to normal error handling
          } else {
            devLog('✅ Session refreshed successfully on retry');
            // Retry the original request with refreshed token
            const retryResponse = await fetch(url, {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${retryData.session.access_token}`,
              },
            });
            return retryResponse;
          }
        }
      
        if (isTransientRefreshError(error)) {
          devLog('⚠️ Failed to refresh session due to transient error:', error);
        } else {
          devError('❌ Failed to refresh session:', error);
        }
        const invalidRefreshToken = isDefinitelyInvalidRefreshToken(error);
        const transientRefreshError = isTransientRefreshError(error);

        if (!invalidRefreshToken || transientRefreshError) {
          devLog('⚠️ Refresh failed due to transient error - preserving session and skipping forced sign-out');
          throw new Error('Session refresh temporary failure - retry later');
        }

        devLog('🚪 Logging out user due to definitely invalid refresh token...');

        // Only clear localStorage on Android when token invalidity is explicit
        const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent);
        if (isAndroid && typeof localStorage !== 'undefined') {
          devLog('🗑️ Clearing stale Android localStorage session (invalid refresh token)');
          localStorage.removeItem('talentfunn-auth');
        }

        await supabase.auth.signOut();
        throw new Error('Session expired - please login again');
      }
    } catch (refreshError) {
      // ✅ CRITICAL: Handle AbortError gracefully during updates
      if (isAbortLikeError(refreshError)) {
        if (justUpdated || updateInProgress) {
          devLog('⚠️ AbortError during refresh after update - this is normal, session should be restored after reload');
          throw new Error('Session refresh aborted during update - will retry after reload');
        }
      }
      // Re-throw other errors
      throw refreshError;
    }

    devLog('✅ Session refreshed successfully, retrying request...');
    
    // Retry with fresh token
    const retryResponse = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${refreshedSession.access_token}`,
      },
    });

    return retryResponse;
  }

  // ✅ NEW: Handle rate limiting (429)
  if (response.status === 429) {
    const errorData = await response.json().catch(() => ({ error: 'For mange forsøk. Prøv igjen senere' }));
    if (!shouldSuppressApiToastDuringSwUpdate(429, errorData.error)) {
      showDedupedApiErrorToast(errorData.error || 'For mange forsøk. Prøv igjen senere');
    } else {
      devLog('ℹ️ Suppressing 429 toast during transient SW update state');
    }
    throw new Error('RATE_LIMITED');
  }

  // ✅ NEW: Handle other HTTP errors (400, 403, 500, etc)
  if (!response.ok && response.status !== 404) { // Allow 404 to pass through for cases where it's expected
    const errorData = await response.json().catch(() => ({ error: 'Noe gikk galt' }));
    
    // Don't show toast for certain errors that are handled in components
    const skipToastForErrors = [
      'E-post må bekreftes', // Email verification - handled in AuthPage
      'Unauthorized', // Auth errors - handled by 401 logic above
      'Cannot send message', // MessagePage maps this to user-friendly text
      'Kan ikke sende melding til denne brukeren', // MessagePage maps this to user-friendly text
      'Denne brukeren er utilgjengelig', // MessagePage maps this to user-friendly text
    ];
    
    const skipForSwUpdate = shouldSuppressApiToastDuringSwUpdate(response.status, errorData.error);
    if (skipForSwUpdate) {
      devLog('ℹ️ Suppressing API error toast during transient SW update state:', response.status);
    }

    if (!skipForSwUpdate && !skipToastForErrors.some(msg => errorData.error?.includes(msg))) {
      showDedupedApiErrorToast(errorData.error || 'Noe gikk galt');
    }
    
    throw new Error(errorData.error || 'Request failed');
  }

  return response;
}
